import { ReactNode } from 'react'
import { useParams } from 'react-router-dom'

import {
  Box,
  Grid,
  ProgressBar,
  Spinner,
  TagColor,
  Text,
  useConfirmationDialog,
} from '@factorialco/deprecated-design-system'
import {
  PerformanceProcessBulkInitiateAgreementsDocument,
  PerformanceProcessMyAgreementsDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { AgreementListCard } from 'modules/performance_process/components/agreements/AgreementListCard'
import { SectionWithStatus } from 'modules/performance_process/components/common/SectionWithStatus'
import { useInitiateAgreement } from 'modules/performance_process/lib/agreementDialog'

const AgreementsGrid: React.FC<{ children: ReactNode }> = ({ children }) => {
  return <Grid variant="medium">{children}</Grid>
}

export const MyAgreements: React.FC<{
  canInitiateAgreemensInBulk: boolean
  republishEvaluationsEnabled: boolean
}> = ({ canInitiateAgreemensInBulk, republishEvaluationsEnabled }) => {
  const { reviewId } = useParams()
  const { access, employee } = useCurrentSession()
  const { openDialog } = useConfirmationDialog()

  const managedByFilter = employee?.id
    ? {
        managerEmployeeId: employee.id,
        onlyDirectReports: true,
      }
    : undefined

  const { data, loading, error } = useQuery(PerformanceProcessMyAgreementsDocument, {
    variables: {
      processId: Number(reviewId),
      currentAccessId: access.id,
      managedByFilter: managedByFilter,
    },
  })

  const [bulkInitiateAgreementsMutation] = useMutation(
    PerformanceProcessBulkInitiateAgreementsDocument,
    {
      refetchQueries: [PerformanceProcessMyAgreementsDocument],
      variables: {
        processId: Number(reviewId),
      },
      extractorFn: (mutationData) =>
        mutationData.performanceMutations.bulkInitiatePerformanceAgreement,
      awaitRefetchQueries: true,
    }
  )

  const bulkInitiateAgreements = async () => {
    openDialog({
      title: i18n.t('performance_process.agreements.my_agreements.bulk_initiate_dialog.title'),
      description: i18n.t(
        'performance_process.agreements.my_agreements.bulk_initiate_dialog.description'
      ),
      onConfirm: async () => await bulkInitiateAgreementsMutation({}),
    })
  }

  const { initiateAgreement } = useInitiateAgreement({ republishEvaluationsEnabled })

  const canInitiate = data?.performance.canBeInitiated || []
  const pending = data?.performance.hasPendingAgreement || []
  const signed = data?.performance.hasSignedAgreement || []

  const total = canInitiate.length + pending.length + signed.length
  const currentAccess = data?.apiCore.access

  if (loading || error)
    return (
      <Box justifyContent="center" alignItems="center" paddingY="s48" height="full">
        <Spinner />
      </Box>
    )

  return (
    <Box gap="s32">
      <Box gap="s12">
        <Text size="500" weight="semibold" color="grey1000">
          {i18n.t('performance_process.agreements.my_agreements.title_new')}
        </Text>
        <Text.Normal>
          {i18n.t('performance_process.agreements.my_agreements.description')}
        </Text.Normal>
      </Box>
      {currentAccess && (
        <Box gap="s48">
          <ProgressBar
            showLabel
            total={total}
            amount={signed.length}
            progressColor="viridian1200"
          />
          {pending && pending.length > 0 && (
            <SectionWithStatus
              tagLabel={i18n.t(
                'performance_process.agreements.my_agreements.category_pending_signature'
              )}
              tagColor={TagColor.malibu}
              count={pending.length}
            >
              <AgreementsGrid>
                {pending.map((target) => (
                  <AgreementListCard
                    managerAccess={currentAccess}
                    target={target}
                    key={target.id}
                  />
                ))}
              </AgreementsGrid>
            </SectionWithStatus>
          )}
          {canInitiate && canInitiate.length > 0 && (
            <SectionWithStatus
              tagLabel={i18n.t(
                'performance_process.agreements.my_agreements.category_can_initiate'
              )}
              tagColor={TagColor.grey}
              count={canInitiate.length}
              action={
                canInitiateAgreemensInBulk
                  ? {
                      label: i18n.t('performance_process.agreements.my_agreements.bulk_initiate'),
                      onClick: bulkInitiateAgreements,
                    }
                  : undefined
              }
            >
              <AgreementsGrid>
                {canInitiate.map((target) => (
                  <AgreementListCard
                    managerAccess={currentAccess}
                    target={target}
                    key={target.id}
                    onInitiate={() =>
                      initiateAgreement({
                        targetName: target.access?.fullName ?? '',
                        targetId: target.id,
                        processId: Number(reviewId),
                      })
                    }
                  />
                ))}
              </AgreementsGrid>
            </SectionWithStatus>
          )}
          {signed && signed.length > 0 && (
            <SectionWithStatus
              tagLabel={i18n.t('performance_process.agreements.my_agreements.category_signed')}
              tagColor={TagColor.correct}
              count={signed.length}
            >
              <AgreementsGrid>
                {signed.map((target) => (
                  <AgreementListCard
                    managerAccess={currentAccess}
                    target={target}
                    key={target.id}
                  />
                ))}
              </AgreementsGrid>
            </SectionWithStatus>
          )}
        </Box>
      )}
    </Box>
  )
}
